import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { FloraSupportMessage } from '../types/flora-support.types';
import { useChatStream } from '../../api/hooks/flora-ai';
import { useChatHistory } from '../../api/hooks/flora-ai/useChatHistory';

const createId = () =>
	typeof crypto !== 'undefined' && crypto.randomUUID
		? crypto.randomUUID()
		: `${Date.now()}-${Math.random().toString(16).slice(2)}`;

interface UseFloraSupportProps {
	userName: string;
	enabled?: boolean;
	permissions?: {
		action: string;
		subject: string;
		inverted?: boolean;
	}[];
}

interface RawHistoryTurn {
	human?: string;
	ai?: string;
	tool?: string;
}

const CHAT_SESSION_KEY = 'chatSessionId';


export const useFloraSupport = ({ enabled = true, permissions = [] }: UseFloraSupportProps) => {
	const [isOpen, setIsOpen] = useState(false);
	const [messages, setMessages] = useState<FloraSupportMessage[]>([]);
	const lastSyncedAtRef = useRef<number | null>(null);


	const [sessionId, setSessionId] = useState<string>(() => {
		const existing = localStorage.getItem(CHAT_SESSION_KEY);
		if (existing) return existing;

		const id = createId();
		localStorage.setItem(CHAT_SESSION_KEY, id);
		return id;
	});

	const { output, sendMessage: sendToWebhook, isStreaming } = useChatStream(sessionId, permissions);
	const { data: historyData, isLoading: isHistoryLoading, isFetched, dataUpdatedAt } = useChatHistory(sessionId);

	const streamingMessageIdRef = useRef<string | null>(null);

	const hasUserReplied = useMemo(
		() => messages.some((message) => message.author === 'user'),
		[messages]
	);


	useEffect(() => {
		if (!enabled || !isFetched) return;
		if (lastSyncedAtRef.current === dataUpdatedAt) return;
		lastSyncedAtRef.current = dataUpdatedAt;

		const rawMessages: RawHistoryTurn[] = historyData?.messages ?? [];
		const historyMessages: FloraSupportMessage[] = [];

		rawMessages.forEach((turn) => {
			if (turn.ai && !turn.ai.startsWith('Calling ')) {
				historyMessages.push({
					id: createId(),
					author: 'support',
					content: turn.ai,
					createdAt: new Date().toISOString(),
				});
			}
			if (turn.human) {
				historyMessages.push({
					id: createId(),
					author: 'user',
					content: turn.human,
					createdAt: new Date().toISOString(),
				});
			}
		});

		setMessages(historyMessages);
	}, [enabled, isFetched, historyData]);

	useEffect(() => {
		const handleStorageChange = (event: StorageEvent) => {
			if (event.key !== CHAT_SESSION_KEY) return;
			const newSessionId = event.newValue;
			if (!newSessionId || newSessionId === sessionId) return;

			streamingMessageIdRef.current = null;
			lastSyncedAtRef.current = null;
			setMessages([]);
			setSessionId(newSessionId);
		};
		window.addEventListener('storage', handleStorageChange);
		return () => window.removeEventListener('storage', handleStorageChange);
	}, [sessionId]);

	const open = useCallback(() => setIsOpen(true), []);
	const close = useCallback(() => setIsOpen(false), []);
	const toggle = useCallback(() => setIsOpen((current) => !current), []);

	const clearSession = useCallback(() => {
		const id = createId();

		localStorage.setItem(CHAT_SESSION_KEY, id)

		streamingMessageIdRef.current = null;
		lastSyncedAtRef.current = null;
		setMessages([]);
		setSessionId(id);
	}, []);

	useEffect(() => {
		if (!output) return;

		setMessages((current) => {
			const messageId = streamingMessageIdRef.current ?? createId();
			streamingMessageIdRef.current = messageId;

			const hasStreamingMessage = current.some((message) => message.id === messageId);

			if (hasStreamingMessage) {
				return current.map((message) =>
					message.id === messageId ? { ...message, content: output } : message
				);
			}

			return [
				...current,
				{ id: messageId, author: 'support', content: output, createdAt: new Date().toISOString() },
			];
		});
	}, [output]);

	const sendMessage = useCallback(
		(content: string) => {
			const trimmed = content.trim();
			if (!trimmed || isStreaming) return;

			streamingMessageIdRef.current = null;

			setMessages((current) => [
				...current,
				{ id: createId(), author: 'user', content: trimmed, createdAt: new Date().toISOString() },
			]);

			sendToWebhook(trimmed);
		},
		[isStreaming, sendToWebhook]
	);

	return {
		isOpen,
		open,
		close,
		toggle,
		messages,
		isLoading: isHistoryLoading,
		isTyping: isStreaming && !output,
		hasUserReplied,
		sendMessage,
		clearSession,
	};
};
